import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import type {
  Role,
  Permission,
  CreateRoleDto,
  UpdateRoleDto,
  PermissionResource,
  PermissionAction,
} from '../types';

// API Functions - all require organizationId for super-admin context
const fetchRoles = async (organizationId: string): Promise<Role[]> => {
  const response = await axiosInstance.get('/permissions/roles', {
    params: { organizationId },
  });
  return response.data;
};

const fetchRole = async (id: string): Promise<Role> => {
  const response = await axiosInstance.get(`/permissions/roles/${id}`);
  return response.data;
};

const fetchPermissions = async (): Promise<Permission[]> => {
  const response = await axiosInstance.get('/permissions');
  return response.data;
};

const createRole = async ({
  organizationId,
  data,
}: {
  organizationId: string;
  data: CreateRoleDto;
}): Promise<Role> => {
  const response = await axiosInstance.post('/permissions/roles', data, {
    params: { organizationId },
  });
  return response.data;
};

const updateRole = async ({
  id,
  data,
}: {
  id: string;
  data: UpdateRoleDto;
}): Promise<Role> => {
  const response = await axiosInstance.patch(`/permissions/roles/${id}`, data);
  return response.data;
};

const deleteRole = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/permissions/roles/${id}`);
};

const cloneRole = async ({
  id,
  organizationId,
}: {
  id: string;
  organizationId: string;
}): Promise<Role> => {
  const response = await axiosInstance.post(`/permissions/roles/${id}/clone`, null, {
    params: { organizationId },
  });
  return response.data;
};

// Query Hooks
export const useRoles = (organizationId: string | null) => {
  return useQuery({
    queryKey: ['roles', organizationId],
    queryFn: () => fetchRoles(organizationId!),
    enabled: !!organizationId,
  });
};

export const useRole = (id: string | null) => {
  return useQuery({
    queryKey: ['roles', id],
    queryFn: () => fetchRole(id!),
    enabled: !!id,
  });
};

export const usePermissions = () => {
  return useQuery({
    queryKey: ['permissions'],
    queryFn: fetchPermissions,
  });
};

// Mutation Hooks
export const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createRole,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['roles', variables.organizationId] });
    },
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateRole,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['roles', data.id] });
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
};

export const useCloneRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cloneRole,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['roles', variables.organizationId] });
    },
  });
};

// Helper: Group permissions by resource
export const groupPermissionsByResource = (
  permissions: Permission[]
): Record<string, Permission[]> => {
  const grouped: Record<string, Permission[]> = {};

  permissions.forEach((permission) => {
    if (!grouped[permission.resource]) {
      grouped[permission.resource] = [];
    }
    grouped[permission.resource].push(permission);
  });

  // Sort actions within each resource
  const actionOrder: PermissionAction[] = ['read', 'create', 'update', 'delete'];
  Object.keys(grouped).forEach((resource) => {
    grouped[resource].sort(
      (a, b) => actionOrder.indexOf(a.action) - actionOrder.indexOf(b.action)
    );
  });

  return grouped;
};

// Resource and Action labels for display
export const RESOURCE_LABELS: Record<PermissionResource, string> = {
  products: 'Products',
  inventory: 'Inventory',
  sales: 'Sales',
  reservations: 'Reservations',
  staff: 'Staff',
  locations: 'Locations',
  reports: 'Reports',
  settings: 'Settings',
  customers: 'Customers',
  b2b: 'B2B Portal',
  deliveries: 'Deliveries',
};

export const ACTION_LABELS: Record<PermissionAction, string> = {
  read: 'View',
  create: 'Create',
  update: 'Edit',
  delete: 'Delete',
};
